/**
 * The "Published" presets on the articles listing.
 *
 * A relative window rather than a date range: readers browsing an editorial
 * directory ask "what's new", not "what shipped between two dates". Each preset
 * resolves to a cutoff at request time and rides the `PublishDate__gt` filter
 * the articles connection already accepts, so nothing has to be aggregated or
 * indexed for it to work.
 */
export interface PublishedWithinOption {
    /** URL value, and the filter state's item value. */
    value: string;
    label: string;
    /** How far back the window reaches. */
    days: number;
}

export const PUBLISHED_WITHIN_OPTIONS: PublishedWithinOption[] = [
    { value: '3-days', label: 'Last 3 days', days: 3 },
    { value: 'week', label: 'Last week', days: 7 },
    { value: 'month', label: 'Last month', days: 30 },
];

export const PUBLISHED_WITHIN_FILTER_KEY = 'publishedWithin';

/** The preset for a URL value, or undefined when it names none of them. */
export const findPublishedWithinOption = (
    value: string | undefined | null,
): PublishedWithinOption | undefined =>
    PUBLISHED_WITHIN_OPTIONS.find((option) => option.value === value);

/**
 * The `PublishDate__gt` cutoff for a preset, as the `YYYY-MM-DD` the articles
 * connection expects. Undefined when no preset is selected — the caller then
 * leaves the variable out entirely, which drops the filter field rather than
 * sending a null one.
 */
export const getPublishedWithinCutoff = (
    value: string | undefined | null,
    now: Date = new Date(),
): string | undefined => {
    const option = findPublishedWithinOption(value);
    if (!option) return undefined;

    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - option.days);
    return cutoff.toISOString().slice(0, 10);
};
